// Explorer — 통합 탐색 페이지 (U32: 루트 기본 화면으로 승격).
// URL 쿼리스트링이 SoT — 해석은 utils/explorerView.ts 단일 출처.
//
// 화면 구조 (2026-10-03 개편, 마스터 지시):
//   SearchBox · Breadcrumb · [+ 새 항목 ▼]
//   FilterBar (타입칩 · 태그칩 · 정렬)
//   ── 일반 탐색 (검색·필터·스마트폴더 아님) ──
//   📁 그룹 구역 (접기 가능, 상태 기억)     전체 보기 = 루트 그룹 / 그룹 화면 = 하위 그룹
//   ★ 즐겨찾기 구역 (강조 박스)            전체 보기 = 전체 즐겨찾기 / 그룹 화면 = 이 그룹의 즐겨찾기
//   🕐 최근 사용 구역                       전체 보기에서만
//   📄 항목 목록 구역                        즐겨찾기·최근 항목도 포함 (중복 제외하지 않음)
//   ── 검색·필터·스마트폴더 ── 결과 목록만 / 휴지통은 TrashView
//
// U42 — 항목 클릭 → useDetailPanel.open() (?item=) → Layout 우측 컬럼 DetailPanel.
// U44 — 다중 선택: Shift/Cmd+클릭, Cmd+A 전체 선택, Esc 해제, 2개 이상이면 MultiSelectToolbar.
// U45 — 멀티 드래그: DndProvider에 선택 상태 등록.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Clock, FileText, Folder, Star } from 'lucide-react';
import { useItems, ITEMS_LIST_KEY } from '../hooks/useItems';
import { useDetailPanel } from '../hooks/useDetailPanel';
import { useMultiSelect } from '../hooks/useMultiSelect';
import type { UseMultiSelectReturn } from '../hooks/useMultiSelect';
import { useDndExt } from '../components/DndProvider';
import { useGroupsTree } from '../hooks/useGroupsTree';
import { SearchBox } from '../components/SearchBox';
import { FilterBar } from '../components/FilterBar';
import { Breadcrumb } from '../components/Breadcrumb';
import { ItemCard } from '../components/ItemCard';
import { MultiSelectToolbar } from '../components/MultiSelectToolbar';
import { Spinner } from '../components/Spinner';
import { NewItemDropdown } from '../components/NewItemDropdown';
import { FolderCardList } from '../components/FolderCard';
import { TrashView } from '../components/TrashView';
import { emitExpandGroups } from '../components/Sidebar';
import { itemsApi } from '../api/items';
import type { ItemMeta } from '../api/items';
import { SHORTCUT_EVENT_CLOSE_OVERLAY } from '../hooks/useGlobalShortcuts';
import type { GroupTreeNode } from '../api/groupsTree';
import {
  listTitle,
  parseExplorerView,
  showsSections,
  toListFilter,
  type ExplorerView,
} from '../utils/explorerView';

const PAGE_SIZE = 24;
/** 그룹 구역 접힘 상태 저장 키 */
const GROUPS_COLLAPSED_KEY = 'locker:explorer:groups-collapsed';

// ─── Explorer 메인 ──────────────────────────────────────

export default function Explorer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = useMemo(() => parseExplorerView(searchParams), [searchParams]);
  const sections = showsSections(view);

  // 검색·태그·타입 필터 활성 시 결과의 group_id를 사이드바에 전달해 자동 펼침
  const searchFilter = { q: view.q, tag: view.tag, type: view.type, limit: 200 };
  const { data: searchData } = useQuery({
    queryKey: ITEMS_LIST_KEY(searchFilter),
    queryFn: () => itemsApi.listItems(searchFilter),
    enabled: view.hasFilter,
  });
  useEffect(() => {
    if (!view.hasFilter) return;
    const groupIds = [
      ...new Set(
        (searchData?.items ?? [])
          .map((item) => item.group_id)
          .filter((id): id is number => id !== null && id > 0),
      ),
    ];
    if (groupIds.length > 0) emitExpandGroups(groupIds);
  }, [view.hasFilter, searchData]);

  const { open: openDetailPanel } = useDetailPanel();

  // U44 — 다중 선택 상태 (범위 선택용 메인 목록 id는 ref로 관리)
  const multiSelect = useMultiSelect();
  const mainItemIdsRef = useRef<number[]>([]);

  // U45 — 멀티 드래그: DndProvider에 선택 상태 등록
  const { registerSelectedIds, registerClearCallback } = useDndExt();
  useEffect(() => {
    registerSelectedIds(multiSelect.selected);
  }, [multiSelect.selected, registerSelectedIds]);
  useEffect(() => {
    registerClearCallback(multiSelect.clear);
  }, [multiSelect.clear, registerClearCallback]);

  // 그룹 이동 (Breadcrumb·폴더 카드)
  const handleGroupClick = useCallback((id: number | null) => {
    multiSelect.clear();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id === null) next.delete('group');
      else next.set('group', String(id));
      next.delete('offset');
      return next;
    }, { replace: true });
  }, [setSearchParams, multiSelect]);

  // 항목 클릭 — 다중 선택 모드 vs 상세 패널
  const handleItemClick = useCallback((id: number, e?: React.MouseEvent) => {
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || multiSelect.selectedCount > 0)) {
      multiSelect.handleItemClick(id, e, mainItemIdsRef.current);
      return;
    }
    openDetailPanel(id);
  }, [openDetailPanel, multiSelect]);

  const handleSelectClick = useCallback((id: number, e: React.MouseEvent) => {
    multiSelect.handleItemClick(id, e, mainItemIdsRef.current);
  }, [multiSelect]);

  // Cmd/Ctrl+A: 현재 목록 전체 선택 (입력 포커스 중 제외)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        const target = document.activeElement;
        if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
        e.preventDefault();
        multiSelect.selectAll(mainItemIdsRef.current);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [multiSelect]);

  // Esc: 선택 전체 해제
  useEffect(() => {
    const handleCloseOverlay = () => {
      if (multiSelect.selectedCount > 0) multiSelect.clear();
    };
    window.addEventListener(SHORTCUT_EVENT_CLOSE_OVERLAY, handleCloseOverlay);
    return () => window.removeEventListener(SHORTCUT_EVENT_CLOSE_OVERLAY, handleCloseOverlay);
  }, [multiSelect]);

  const cardProps = { onItemClick: handleItemClick, multiSelect, onSelectClick: handleSelectClick };

  return (
    <div className="explorer stack">
      {/* 상단: 검색바 + 빵부스러기 + 새 항목 드롭다운 */}
      <div className="explorer__top-bar">
        <SearchBox placeholder="이름·내용으로 전체 검색" className="explorer__search" />
        <Breadcrumb groupId={view.groupId} onGroupClick={handleGroupClick} />
        <div className="explorer__actions">
          <NewItemDropdown />
        </div>
      </div>

      <FilterBar />

      {view.smart === 'trash' ? (
        <TrashView />
      ) : (
        <>
          {sections && <GroupsSection groupId={view.groupId} onGroupNavigate={handleGroupClick} />}
          {sections && <PinnedSection groupId={view.groupId} {...cardProps} />}
          {sections && view.groupId === null && <RecentSection {...cardProps} />}
          <MainListSection view={view} mainItemIdsRef={mainItemIdsRef} {...cardProps} />
        </>
      )}

      {/* U44 — 다중 선택 툴바 (선택 2개 이상 시 표시) */}
      <MultiSelectToolbar
        selected={multiSelect.selected}
        selectedCount={multiSelect.selectedCount}
        onClear={multiSelect.clear}
      />
    </div>
  );
}

// ─── 공통: 구역 껍데기 ───────────────────────────────────

interface ExplorerSectionProps {
  icon: ReactNode;
  title: string;
  /** 제목 옆 개수 배지 (없으면 생략) */
  count?: number;
  /** 'pinned' = 노란 강조 박스 */
  variant?: 'default' | 'pinned';
  /** 접기 토글 (지정 시 헤더가 버튼) */
  collapsed?: boolean;
  onToggle?: () => void;
  testId?: string;
  children: ReactNode;
}

/**
 * ExplorerSection — 구역 헤더(아이콘·제목·개수·접기) + 본문.
 * 구역마다 구분선·여백을 두어 즐겨찾기/최근/목록이 시각적으로 나뉘게 한다.
 */
function ExplorerSection({
  icon, title, count, variant = 'default', collapsed, onToggle, testId, children,
}: ExplorerSectionProps) {
  const collapsible = onToggle !== undefined;
  const headerInner = (
    <>
      {collapsible && (
        <span className="explorer-section__chevron" aria-hidden="true">
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </span>
      )}
      <span className="explorer-section__icon" aria-hidden="true">{icon}</span>
      <h3 className="explorer-section__title">{title}</h3>
      {count !== undefined && <span className="explorer-section__count">{count}</span>}
    </>
  );

  return (
    <section
      className={`explorer-section explorer-section--${variant}`}
      aria-label={title}
      data-testid={testId}
    >
      {collapsible ? (
        <button
          type="button"
          className="explorer-section__header explorer-section__header--toggle"
          onClick={onToggle}
          aria-expanded={!collapsed}
        >
          {headerInner}
        </button>
      ) : (
        <header className="explorer-section__header">{headerInner}</header>
      )}
      {!collapsed && <div className="explorer-section__body">{children}</div>}
    </section>
  );
}

interface CardListProps {
  onItemClick: (id: number, e?: React.MouseEvent) => void;
  multiSelect: UseMultiSelectReturn;
  onSelectClick: (id: number, e: React.MouseEvent) => void;
}

/** 카드 그리드 (구역 공용) */
function CardGrid({ items, onItemClick, multiSelect, onSelectClick, searchQuery }: CardListProps & {
  items: ItemMeta[];
  searchQuery?: string;
}) {
  const isSelectMode = multiSelect.selectedCount > 0;
  return (
    <div className="item-list__grid">
      {items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          onClick={isSelectMode ? undefined : (id) => onItemClick(id)}
          onSelectClick={isSelectMode ? onSelectClick : undefined}
          selected={multiSelect.selected.has(item.id)}
          searchQuery={searchQuery}
          showPayloadMatch={!!searchQuery}
        />
      ))}
    </div>
  );
}

// ─── 📁 그룹 구역 ────────────────────────────────────────

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(GROUPS_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * GroupsSection — 전체 보기: 루트 그룹 / 그룹 화면: 하위 그룹. 접기 상태는 localStorage에 기억.
 * 표시할 그룹이 없으면 렌더하지 않는다.
 */
function GroupsSection({ groupId, onGroupNavigate }: {
  groupId: number | null;
  onGroupNavigate: (id: number | null) => void;
}) {
  const { data: tree = [] } = useGroupsTree('credential');
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(GROUPS_COLLAPSED_KEY, c ? '0' : '1');
      } catch {
        // 저장 실패(사생활 모드 등)는 무시 — 이번 세션에서만 유지
      }
      return !c;
    });
  };

  const nodes: GroupTreeNode[] = groupId === null ? tree : findNodeChildren(tree, groupId);
  if (nodes.length === 0) return null;

  return (
    <ExplorerSection
      icon={<Folder size={15} />}
      title={groupId === null ? '그룹' : '하위 그룹'}
      count={nodes.length}
      collapsed={collapsed}
      onToggle={toggle}
      testId="explorer-groups"
    >
      <FolderCardList nodes={nodes} onNavigate={onGroupNavigate} />
    </ExplorerSection>
  );
}

/** 트리에서 특정 그룹의 자식 노드 배열 (없으면 빈 배열) */
function findNodeChildren(nodes: GroupTreeNode[], targetId: number): GroupTreeNode[] {
  for (const node of nodes) {
    if (node.id === targetId) return node.children;
    const found = findNodeChildren(node.children, targetId);
    if (found.length > 0) return found;
  }
  return [];
}

// ─── ★ 즐겨찾기 구역 ─────────────────────────────────────

/**
 * PinnedSection — 전체 보기: 전체 즐겨찾기 / 그룹 화면: 이 그룹(하위 제외)의 즐겨찾기.
 * 즐겨찾기가 없으면 렌더하지 않는다. 아래 목록에서도 같은 항목이 다시 보인다(의도).
 */
function PinnedSection({ groupId, ...cardProps }: CardListProps & { groupId: number | null }) {
  const { data, isLoading } = useItems({
    pinned: 1,
    ...(groupId !== null ? { group_id: groupId } : {}),
    sort: 'name',
    limit: 24,
  });
  const items = data?.items ?? [];
  if (!isLoading && items.length === 0) return null;

  return (
    <ExplorerSection
      icon={<Star size={15} fill="currentColor" />}
      title={groupId === null ? '즐겨찾기' : '이 그룹의 즐겨찾기'}
      count={items.length}
      variant="pinned"
      testId="explorer-pinned"
    >
      {isLoading ? <Spinner /> : <CardGrid items={items} {...cardProps} />}
    </ExplorerSection>
  );
}

// ─── 🕐 최근 사용 구역 (전체 보기 전용) ───────────────────

function RecentSection(cardProps: CardListProps) {
  const { data, isLoading } = useItems({ sort: 'last_used_at', limit: 6 });
  const items = (data?.items ?? []).filter((item) => item.last_used_at !== null);
  if (!isLoading && items.length === 0) return null;

  return (
    <ExplorerSection icon={<Clock size={15} />} title="최근 사용" count={items.length} testId="explorer-recent">
      {isLoading ? <Spinner /> : <CardGrid items={items} {...cardProps} />}
    </ExplorerSection>
  );
}

// ─── 📄 항목 목록 구역 ───────────────────────────────────

function MainListSection({ view, mainItemIdsRef, ...cardProps }: CardListProps & {
  view: ExplorerView;
  mainItemIdsRef: React.MutableRefObject<number[]>;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const groupScope = searchParams.get('scope') === 'descendants' ? 'descendants' : undefined;

  // "최근 N일" 기준 시각은 분 단위로 고정 — 렌더마다 Date.now()가 바뀌면 쿼리 키가 계속 달라져 무한 재조회
  const minuteNow = Math.floor(Date.now() / 60_000) * 60_000;
  const filter = toListFilter(view, { limit: PAGE_SIZE, now: minuteNow, groupScope });
  const { data, isLoading, isError } = useItems(filter);
  const items = data?.items ?? [];

  // 범위 선택·전체 선택용 id 동기화
  const idsKey = items.map((i) => i.id).join(',');
  useEffect(() => {
    mainItemIdsRef.current = idsKey ? idsKey.split(',').map(Number) : [];
  }, [idsKey, mainItemIdsRef]);

  const setOffset = (next: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next <= 0) p.delete('offset');
      else p.set('offset', String(next));
      return p;
    }, { replace: true });
  };

  const paged = view.offset > 0 || items.length === PAGE_SIZE;
  const countLabel = paged ? undefined : items.length;

  return (
    <ExplorerSection
      icon={<FileText size={15} />}
      title={listTitle(view)}
      count={isLoading ? undefined : countLabel}
      testId="explorer-list"
    >
      {isLoading ? (
        <div className="item-list item-list--loading"><Spinner /></div>
      ) : isError ? (
        <div className="item-list item-list--error">
          <p className="muted">항목을 불러오는 중 오류가 발생했습니다.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="item-list item-list--empty">
          <p className="muted">항목이 없습니다.</p>
        </div>
      ) : (
        <div className="item-list">
          {view.q && (
            <div className="item-list__search-info">
              <span className="muted">"{view.q}" 검색 결과 {items.length}건</span>
            </div>
          )}
          <CardGrid items={items} searchQuery={view.q} {...cardProps} />
          {paged && (
            <div className="item-list__pagination">
              <button type="button" className="btn btn--ghost" onClick={() => setOffset(view.offset - PAGE_SIZE)} disabled={view.offset === 0}>
                이전
              </button>
              <span className="muted">{view.offset + 1}–{view.offset + items.length}</span>
              <button type="button" className="btn btn--ghost" onClick={() => setOffset(view.offset + PAGE_SIZE)} disabled={items.length < PAGE_SIZE}>
                다음
              </button>
            </div>
          )}
        </div>
      )}
    </ExplorerSection>
  );
}
